import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import api from '../lib/api';
import { isAxiosError } from 'axios';

export interface UserMemory {
  nickName?: string;
  role?: string;
  communicationPreference?: string;
  otherNotes?: string[];
}

export interface Memory {
  memory?: UserMemory;
  answeredAllOnboardingQuestions?: boolean;
  skippedOnboardingQuestions?: boolean;
  lastAnsweredAllQuestionsAt?: string;
}

export interface MemoryUpdateRequest {
  memory?: UserMemory;
  answeredAllOnboardingQuestions?: boolean;
  skippedOnboardingQuestions?: boolean;
  lastAnsweredAllQuestionsAt?: string;
}

export interface MemoryUpdateResponse {
  data: MemoryUpdateRequest;
}

export const MEMORY_QUERY_KEY = ['memory'];

export const useMemory = (
  options?: Partial<UseQueryOptions<Memory | undefined, Error>>,
) => {
  return useQuery({
    queryKey: MEMORY_QUERY_KEY,
    queryFn: async (): Promise<Memory | undefined> => {
      try {
        const response = await api.get('/api/core/pleri/memory');
        return response.data.data;
      } catch (error) {
        if (isAxiosError(error)) {
          if (error.response?.status === 404) {
            return;
          }
        }
        throw error;
      }
    },
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useUpdateMemory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      params: MemoryUpdateRequest,
    ): Promise<MemoryUpdateRequest> => {
      const response = await api.put<MemoryUpdateResponse>(
        '/api/core/pleri/memory',
        params,
      );
      await queryClient.refetchQueries({ queryKey: MEMORY_QUERY_KEY });
      // Return the updated data from response
      return response.data.data;
    },
  });
};
